import { getOpticalMeasureSettings } from '../src/ExpoOpticalMeasure.settings'
import type { OpticalMeasureSettings } from '../src/ExpoOpticalMeasure.types'
import {
  applyShSettingsBaseline,
  readShSettingsPath,
  SH_BASELINE_1_1,
  SH_BASELINE_DIFFERING_PATHS,
} from './shSettingsBaseline'
import type { ShTestingSettingsSelection } from './types'

const BASELINE_OPTIONS = ['1.1', '1.2'] as const
const DISTANCE_TOLERANCE_OPTIONS = ['current', 'tight', 'tightest'] as const
const PITCH_CORRECTION_OPTIONS = ['off', 'on'] as const
const CAPTURE_SAMPLES_OPTIONS = ['5', '9', '13'] as const
const SCALE_REFERENCE_OPTIONS = [
  'vbox-first',
  'balanced',
  'pd-guarded',
] as const
const PITCH_REFERENCE_OPTIONS = ['neutral', 'look-up-3', 'look-up-6'] as const

type ScaleReference = ShTestingSettingsSelection['scaleReference']
type PitchReference = ShTestingSettingsSelection['pitchReference']
type DistanceTolerance = ShTestingSettingsSelection['distanceTolerance']
type PitchCorrection = ShTestingSettingsSelection['pitchCorrection']

/**
 * `sh.correction.lensDepthMm` — the pupil-to-lens-plane depth the vertex
 * parallax term uses. `on` is 22 mm, which is what the measured slope implies
 * (0.389 mm/deg = depth × cos(8°) × π/180 → 22.5) and is anatomically right for
 * a cornea offset of 12 mm plus a ~12 mm vertex distance.
 *
 * Slightly under the fit on purpose: over-correcting reintroduces pitch
 * sensitivity with the sign reversed, which is worse than under-correcting.
 */
const PITCH_CORRECTION_PRESETS = {
  off: 0,
  on: 22,
} as const satisfies Record<PitchCorrection, number>

/**
 * How far off the target distance (50 cm, or 35 cm with a VBOX) a subject may
 * be. The target itself never moves — these are the bands around it, and the
 * shipped ones are wide enough that a subject can sit well outside the
 * distance the SH pipeline was tuned for and still be captured.
 *
 * Every band is applied at once, so a preset tightens the same distance in
 * all three places it is enforced:
 *
 * - `guidanceCm` — `framing.*.guidanceDistanceToleranceCm`, the band
 *   `deriveGuidancePhase` prompts "move closer"/"move back" outside of.
 * - `captureCm` — `framing.*.captureDistanceToleranceCm`, the band native
 *   hard-rejects frames outside of (`distance-out-of-range`).
 * - `sh.scoring.qualityDistanceToleranceCm` — where the distance component of
 *   candidate quality reaches 0. Kept equal to `captureCm`, which is the
 *   relationship the shipped defaults already have (both 20 cm), so a frame
 *   at the edge of what is capturable scores nothing for distance.
 */
const DISTANCE_TOLERANCE_PRESETS = {
  current: { guidanceCm: 15, captureCm: 20 },
  tight: { guidanceCm: 10, captureCm: 14 },
  // The shipped one. The capture band sits 1 cm outside the guidance band, not
  // 3: subjects settle on the boundary that is *enforced*, not the one they are
  // guided to, so a wide margin just relocates the problem into it — measured,
  // 10 of 12 captures landed between the two. The margin exists only to absorb
  // frame-to-frame jitter, and only needs to be wide enough for that now that
  // guidance stays live through sampling.
  tightest: { guidanceCm: 6, captureCm: 7 },
} as const satisfies Record<
  DistanceTolerance,
  { guidanceCm: number; captureCm: number }
>

/**
 * `geometry.rotationXOffsetDeg` — the head pitch the pose gates treat as
 * neutral.
 *
 * ARKit's zero is head level relative to the camera axis, but the subject is
 * looking at the oval, their own preview and the bottom sheet, all of which sit
 * roughly 12° below that axis at the 50 cm working distance. The habitual pose
 * is therefore chin-down, and levelling is the most the current gates ask for.
 * A positive offset makes a level head read as chin-down, so the guidance and
 * gates settle the subject chin-*up*, looking nearer the camera.
 *
 * The offset is applied at the source, so it also enters
 * `tiltCorrectionFactor`: at `look-up-3` a subject who zeroes the gate is
 * physically 3° chin-up, but SH is still corrected as if level — an over-read
 * of about 0.6% (~0.15 mm on a 25 mm SH). Small, but it grows with the offset,
 * which is why the steps stay short and `neutral` remains the default.
 */
const PITCH_REFERENCE_PRESETS = {
  neutral: 0,
  'look-up-3': 3,
  'look-up-6': 6,
} as const satisfies Record<PitchReference, number>

/**
 * VBOX-vs-PD agreement bands per preset. `vbox-first` matches the shipped
 * defaults: the VBOX scale is the physical reference and only a plainly broken
 * span hands the frame back to the PD scale. The tighter presets exist to
 * compare against that on the same device.
 */
const SCALE_REFERENCE_PRESETS = {
  'vbox-first': { trustRatioDeviation: 0.35, maxScaleRatioDeviation: 0.6 },
  balanced: { trustRatioDeviation: 0.2, maxScaleRatioDeviation: 0.35 },
  'pd-guarded': { trustRatioDeviation: 0.08, maxScaleRatioDeviation: 0.2 },
} as const satisfies Record<
  ScaleReference,
  { trustRatioDeviation: number; maxScaleRatioDeviation: number }
>

export const isShTestingSettingsSelection = (
  value: unknown
): value is ShTestingSettingsSelection => {
  if (!value || typeof value !== 'object') {
    return false
  }

  const candidate = value as Partial<ShTestingSettingsSelection>
  return (
    BASELINE_OPTIONS.some((option) => option === candidate.baseline) &&
    DISTANCE_TOLERANCE_OPTIONS.some(
      (option) => option === candidate.distanceTolerance
    ) &&
    PITCH_CORRECTION_OPTIONS.some(
      (option) => option === candidate.pitchCorrection
    ) &&
    CAPTURE_SAMPLES_OPTIONS.some(
      (option) => option === candidate.captureSamples
    ) &&
    SCALE_REFERENCE_OPTIONS.some(
      (option) => option === candidate.scaleReference
    ) &&
    PITCH_REFERENCE_OPTIONS.some(
      (option) => option === candidate.pitchReference
    )
  )
}

/**
 * Read from the paths where the two frozen columns actually disagree — the
 * ones that can tell them apart. Anything else reports `1.2`, the same way an
 * unrecognised sample count falls back to the shipped chip: a half-1.1 settings
 * object is not a 1.1 baseline, and saying so would make "Modified" lie.
 */
const resolveBaseline = (
  settings: OpticalMeasureSettings
): ShTestingSettingsSelection['baseline'] =>
  SH_BASELINE_DIFFERING_PATHS.length > 0 &&
  SH_BASELINE_DIFFERING_PATHS.every(
    (path) =>
      readShSettingsPath(settings, path) ===
      readShSettingsPath(SH_BASELINE_1_1, path)
  )
    ? '1.1'
    : '1.2'

/** Nearest preset, so hand-tuned values still map to a chip. */
const resolvePitchReference = (
  settings: OpticalMeasureSettings
): PitchReference => {
  const offsetDeg = settings.geometry.rotationXOffsetDeg
  const [nearest] = PITCH_REFERENCE_OPTIONS.map((option) => ({
    option,
    distance: Math.abs(PITCH_REFERENCE_PRESETS[option] - offsetDeg),
  })).sort((a, b) => a.distance - b.distance)
  return nearest.option
}

/**
 * Nearest preset across both profiles' bands, so hand-tuned values still map
 * to a chip whichever profile the flow is currently running.
 */
const resolveDistanceTolerance = (
  settings: OpticalMeasureSettings
): DistanceTolerance => {
  const profiles = [settings.framing.normal, settings.framing.vbox]
  const [nearest] = DISTANCE_TOLERANCE_OPTIONS.map((option) => ({
    option,
    distance: profiles.reduce(
      (total, profile) =>
        total +
        Math.abs(
          DISTANCE_TOLERANCE_PRESETS[option].guidanceCm -
            profile.guidanceDistanceToleranceCm
        ) +
        Math.abs(
          DISTANCE_TOLERANCE_PRESETS[option].captureCm -
            profile.captureDistanceToleranceCm
        ),
      0
    ),
  })).sort((a, b) => a.distance - b.distance)
  return nearest.option
}

/** Nearest preset by max deviation, so hand-tuned values still map to a chip. */
const resolveScaleReference = (
  settings: OpticalMeasureSettings
): ScaleReference => {
  const { maxScaleRatioDeviation } = settings.sh.scale
  const [nearest] = SCALE_REFERENCE_OPTIONS.map((option) => ({
    option,
    distance: Math.abs(
      SCALE_REFERENCE_PRESETS[option].maxScaleRatioDeviation -
        maxScaleRatioDeviation
    ),
  })).sort((a, b) => a.distance - b.distance)
  return nearest.option
}

export const resolveShTestingSettings = (
  settings: OpticalMeasureSettings
): ShTestingSettingsSelection => {
  const captureSamples = CAPTURE_SAMPLES_OPTIONS.some(
    (option) => Number(option) === settings.sh.capture.targetCandidateFrames
  )
    ? String(settings.sh.capture.targetCandidateFrames)
    : '9'

  return {
    baseline: resolveBaseline(settings),
    distanceTolerance: resolveDistanceTolerance(settings),
    pitchCorrection: settings.sh.correction.lensDepthMm > 0 ? 'on' : 'off',
    captureSamples:
      captureSamples as ShTestingSettingsSelection['captureSamples'],
    scaleReference: resolveScaleReference(settings),
    pitchReference: resolvePitchReference(settings),
  }
}

export const DEFAULT_SH_TESTING_SETTINGS = resolveShTestingSettings(
  getOpticalMeasureSettings(false)
)

/**
 * Every row at its 1.1-equivalent chip, so a full revert is one tap rather than
 * six. A literal for the same reason `SH_BASELINE_1_1` is: resolving it from
 * the snapshot would make the two definitions of "1.1" agree by construction
 * instead of by test. `shSettingsBaseline.test.ts` pins it against the snapshot.
 */
export const SH_TESTING_SETTINGS_1_1: ShTestingSettingsSelection = {
  baseline: '1.1',
  distanceTolerance: 'current',
  // 1.1 predates the parallax term entirely.
  pitchCorrection: 'off',
  captureSamples: '5',
  scaleReference: 'vbox-first',
  pitchReference: 'neutral',
}

const isSameShTestingSettings = (
  selection: ShTestingSettingsSelection,
  other: ShTestingSettingsSelection
) =>
  selection.baseline === other.baseline &&
  selection.distanceTolerance === other.distanceTolerance &&
  selection.pitchCorrection === other.pitchCorrection &&
  selection.captureSamples === other.captureSamples &&
  selection.scaleReference === other.scaleReference &&
  selection.pitchReference === other.pitchReference

export const isDefaultShTestingSettings = (
  selection: ShTestingSettingsSelection
) => isSameShTestingSettings(selection, DEFAULT_SH_TESTING_SETTINGS)

/** Drives the "Load 1.1" button's disabled state. */
export const isShTestingSettings11 = (selection: ShTestingSettingsSelection) =>
  isSameShTestingSettings(selection, SH_TESTING_SETTINGS_1_1)

/** Both profiles keep their target distance; only the bands around it move. */
const applyDistanceTolerancePreset = (
  settings: OpticalMeasureSettings,
  preset: ShTestingSettingsSelection['distanceTolerance']
): OpticalMeasureSettings => {
  const { guidanceCm, captureCm } = DISTANCE_TOLERANCE_PRESETS[preset]
  const withBands = (profile: OpticalMeasureSettings['framing']['normal']) => ({
    ...profile,
    guidanceDistanceToleranceCm: guidanceCm,
    captureDistanceToleranceCm: captureCm,
  })

  return {
    ...settings,
    framing: {
      ...settings.framing,
      normal: withBands(settings.framing.normal),
      vbox: withBands(settings.framing.vbox),
    },
    sh: {
      ...settings.sh,
      scoring: {
        ...settings.sh.scoring,
        qualityDistanceToleranceCm: captureCm,
      },
    },
  }
}

/**
 * Every knob has exactly one control. The Baseline row goes on **first** and
 * writes the whole snapshot; each later row then rewrites the paths it owns, so
 * a row selection always wins. What survives from the baseline is the residual
 * — `finalFrameMedianWeight`, `maxSampleSpreadMm`, `gates.maxDifferenceMm`,
 * `correction.includeHeadPitch`, and now `framing.position.*` — which no row
 * exposes.
 *
 * Without that ordering `resolveShTestingSettings` would be ambiguous: a
 * settings object could match 1.1 on some paths and a 1.2 row on others, and
 * the chip it lit would depend on which comparison ran first.
 *
 * The Framing and Frame Aggregation rows were removed rather than hidden.
 * A row that is not rendered but still applied is the `tight` trap: a stored
 * value the tester cannot see, silently outranking every shipped default.
 * `framing.position.*` is covered by the baseline snapshot; `eyeOpenMin` and
 * `multiFrameMedianEnabled` simply keep their defaults now.
 */
export const applyShTestingSettings = (
  settings: OpticalMeasureSettings,
  selection: ShTestingSettingsSelection
): OpticalMeasureSettings => {
  const withFraming = applyDistanceTolerancePreset(
    applyShSettingsBaseline(settings, selection.baseline),
    selection.distanceTolerance
  )

  return {
    ...withFraming,
    geometry: {
      ...withFraming.geometry,
      rotationXOffsetDeg: PITCH_REFERENCE_PRESETS[selection.pitchReference],
    },
    sh: {
      ...withFraming.sh,
      capture: {
        ...withFraming.sh.capture,
        targetCandidateFrames: Number(selection.captureSamples),
      },
      correction: {
        ...withFraming.sh.correction,
        lensDepthMm: PITCH_CORRECTION_PRESETS[selection.pitchCorrection],
      },
      scale: {
        ...withFraming.sh.scale,
        ...SCALE_REFERENCE_PRESETS[selection.scaleReference],
      },
    },
  }
}
